import React from 'react';
import type { Customer } from '../../types';
import {
    ArrowRight,
    CheckCircle,
    MessageSquare,
    Loader2,
    FileText,
    AlertCircle,
    Upload,
    Eye,
    XCircle,
    Download,
} from 'lucide-react';
import { useNotification } from '../../contexts/NotificationContext';
import { customersApi } from '../../services/api';

const interventionTypes = [
    { value: 'annulation_client', label: 'Annulation client' },
    { value: 'demande_documents', label: 'Demande de documents' },
    { value: 'informations_manquantes', label: 'Informations manquantes' },
    { value: 'autre', label: 'Autre' }
];

const getInterventionTypeLabel = (type: string): string => {
    const found = interventionTypes.find(t => t.value === type);
    return found ? found.label : type;
};

const getStateName = (stateNumber: number | string | null | undefined): string => {
    if (!stateNumber) return 'État inconnu';
    const state = typeof stateNumber === 'string' ? parseInt(stateNumber) : stateNumber;
    switch(state) {
        case 1: return 'État 1 - Contrôle administratif';
        case 2: return 'État 2 - Soumission institutions';
        case 3: return 'État 3 - Réponse institutions';
        case 4: return 'État 4 - Finalisation';
        default: return `État ${state}`;
    }
};

export interface WorkflowActionsTabProps {
    customer: Customer;
    currentState: number;
    getFileUrl: (filePath: string | undefined) => string;

    // State transition
    stateTransitionIsPending: boolean;
    handleStateTransition: (newState: number) => void;

    // State 2 - AEIS submission
    isState2Submitting: boolean;
    handleState2Submit: () => void;
    aeisResult: { success: boolean; message: string; screenshot?: string | null; demande_suppletive?: string; email_result?: { success: boolean; message: string } | null } | null;
    aeisIsSubmitting: boolean;

    // State 3
    centraleValidated: boolean;
    suppletiveValidated: boolean;
    noCaisseInResponse: boolean;
    setNoCaisseInResponse: (val: boolean) => void;
    selectedCaisses: string[];
    setSelectedCaisses: React.Dispatch<React.SetStateAction<string[]>>;
    caisseSearch: string;
    setCaisseSearch: (val: string) => void;
    isMissingDocuments: boolean;
    setIsMissingDocuments: (val: boolean) => void;
    selectedAmount: 'more' | 'less' | 'zero' | null;
    setSelectedAmount: (val: 'more' | 'less' | 'zero' | null) => void;
    hasOldTransfert: boolean | null;
    setHasOldTransfert: (val: boolean | null) => void;
    totalAmount: string;
    setTotalAmount: (val: string) => void;
    selectedPensionFund: string;
    setSelectedPensionFund: (val: string) => void;
    oldTransfertAmount: string;
    setOldTransfertAmount: (val: string) => void;
    pensionFundSearch: string;
    setPensionFundSearch: (val: string) => void;
    caissesList: Array<{ id: number; caisse: string; npa?: string; localite?: string }>;
    handleValidateCentrale: () => void;
    handleValidateSuppletive: () => void;
    state3SubmissionIsPending: boolean;

    // Intervention
    comment: string;
    setComment: (val: string) => void;
    setShowInterventionModal: (val: boolean) => void;
}


const WorkflowActionsTab: React.FC<WorkflowActionsTabProps> = ({
    customer,
    currentState,
    getFileUrl,
    stateTransitionIsPending,
    handleStateTransition,
    isState2Submitting,
    handleState2Submit,
    aeisResult,
    aeisIsSubmitting,
    centraleValidated,
    suppletiveValidated,
    noCaisseInResponse,
    setNoCaisseInResponse,
    selectedCaisses,
    setSelectedCaisses,
    caisseSearch,
    setCaisseSearch,
    isMissingDocuments,
    setIsMissingDocuments,
    selectedAmount,
    setSelectedAmount,
    hasOldTransfert,
    setHasOldTransfert,
    totalAmount,
    setTotalAmount,
    selectedPensionFund,
    setSelectedPensionFund,
    oldTransfertAmount,
    setOldTransfertAmount,
    pensionFundSearch,
    setPensionFundSearch,
    caissesList,
    handleValidateCentrale,
    handleValidateSuppletive,
    state3SubmissionIsPending,
    comment,
    setComment,
    setShowInterventionModal,
}) => {
    const notify = useNotification();

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* LEFT COLUMN - State-specific Actions (1/2 width) */}
            <div className="space-y-6 relative">
                {/* Intervention blocking overlay - only when there's an active intervention */}
                {customer?.intervention_conseiller && (
                    <div className="absolute inset-0 bg-gray-100/80 backdrop-blur-[1px] z-10 rounded-lg flex items-center justify-center">
                        <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-300 text-center max-w-sm">
                            <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                            <p className="text-sm font-medium text-gray-900">
                                Intervention en cours
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                                Les actions sont désactivées jusqu'à la clôture de l'intervention
                            </p>
                        </div>
                    </div>
                )}

                {/* State 1 */}
                {currentState === 1 && (
                    <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
                            <h3 className="text-lg font-semibold text-white">État 1 - Contrôle administratif</h3>
                            <p className="text-xs text-blue-100 mt-1">Vérification et validation du dossier</p>
                        </div>
                        <div className="p-6">
                            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-5 mb-6">
                                <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Tâches à effectuer
                                </h4>
                                <ul className="space-y-2.5">
                                    <li className="flex items-start text-sm text-blue-900">
                                        <span className="inline-block w-1.5 h-1.5 bg-blue-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                                        <span>Vérifier les informations personnelles du client</span>
                                    </li>
                                    <li className="flex items-start text-sm text-blue-900">
                                        <span className="inline-block w-1.5 h-1.5 bg-blue-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                                        <span>Contrôler la validité des documents d'identité</span>
                                    </li>
                                    <li className="flex items-start text-sm text-blue-900">
                                        <span className="inline-block w-1.5 h-1.5 bg-blue-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                                        <span>Valider les données de contact</span>
                                    </li>
                                    <li className="flex items-start text-sm text-blue-900">
                                        <span className="inline-block w-1.5 h-1.5 bg-blue-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                                        <span>Soumettre le dossier aux institutions</span>
                                    </li>
                                </ul>
                            </div>

                            {/* Documents à consulter */}
                            <div className="mb-6">
                                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                                    <FileText className="w-4 h-4 mr-2 text-blue-600" />
                                    Documents à consulter
                                </h4>
                                <div className="grid grid-cols-2 gap-3">
                                    {customer.photo && (
                                        <a
                                            href={getFileUrl(customer.photo)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-blue-50 hover:border-blue-200 border border-gray-200 transition-colors group"
                                        >
                                            <Eye className="w-4 h-4 text-gray-400 group-hover:text-blue-600 mr-2" />
                                            <span className="text-sm text-gray-700 group-hover:text-blue-700">Pièce d'identité</span>
                                        </a>
                                    )}
                                    {customer.photo2 && (
                                        <a
                                            href={getFileUrl(customer.photo2)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-blue-50 hover:border-blue-200 border border-gray-200 transition-colors group"
                                        >
                                            <Eye className="w-4 h-4 text-gray-400 group-hover:text-blue-600 mr-2" />
                                            <span className="text-sm text-gray-700 group-hover:text-blue-700">Pièce d'identité (verso)</span>
                                        </a>
                                    )}
                                    {customer.procuration && (
                                        <a
                                            href={getFileUrl(customer.procuration)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-blue-50 hover:border-blue-200 border border-gray-200 transition-colors group"
                                        >
                                            <Eye className="w-4 h-4 text-gray-400 group-hover:text-blue-600 mr-2" />
                                            <span className="text-sm text-gray-700 group-hover:text-blue-700">Procuration</span>
                                        </a>
                                    )}
                                    {customer.libre_passage && (
                                        <a
                                            href={getFileUrl(customer.libre_passage)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-blue-50 hover:border-blue-200 border border-gray-200 transition-colors group"
                                        >
                                            <Eye className="w-4 h-4 text-gray-400 group-hover:text-blue-600 mr-2" />
                                            <span className="text-sm text-gray-700 group-hover:text-blue-700">Demande centrale</span>
                                        </a>
                                    )}
                                </div>
                                {!customer.photo && !customer.photo2 && !customer.procuration && !customer.libre_passage && (
                                    <p className="text-sm text-gray-500 italic">Aucun document disponible</p>
                                )}
                            </div>

                            <button
                                onClick={() => handleStateTransition(2)}
                                disabled={stateTransitionIsPending}
                                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3.5 rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 flex items-center justify-center shadow-md hover:shadow-lg transition-all font-medium"
                            >
                                {stateTransitionIsPending ? (
                                    <>
                                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                        Soumission en cours...
                                    </>
                                ) : (
                                    <>
                                        <ArrowRight className="w-5 h-5 mr-2" />
                                        Soumettre le dossier
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* State 2 */}
                {currentState === 2 && (
                    <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                        <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4">
                            <h3 className="text-lg font-semibold text-white">État 2 - Soumission institutions</h3>
                            <p className="text-xs text-green-100 mt-1">Upload et envoi de la demande</p>
                        </div>
                        <div className="p-6">
                            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-5 mb-6">
                                <h4 className="text-sm font-semibold text-green-900 mb-3 flex items-center">
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Tâches à effectuer
                                </h4>
                                <ul className="space-y-2.5">
                                    <li className="flex items-start text-sm text-green-900">
                                        <span className="inline-block w-1.5 h-1.5 bg-green-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                                        <span>Création de la demande à la supplétive</span>
                                    </li>
                                    <li className="flex items-start text-sm text-green-900">
                                        <span className="inline-block w-1.5 h-1.5 bg-green-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                                        <span>Création de la demande à la centrale (un mail sera envoyé)</span>
                                    </li>
                                </ul>
                            </div>

                            {/* AEIS Submission */}
                            <div className="space-y-4">
                                {/* AEIS Result Display */}
                                {aeisResult && (
                                    <div className="space-y-3">
                                        {/* Status messages */}
                                        <div className={`p-4 rounded-lg border ${aeisResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                                            <p className={`text-sm font-medium flex items-center ${aeisResult.success ? 'text-green-700' : 'text-red-700'}`}>
                                                {aeisResult.success ? (
                                                    <CheckCircle className="w-4 h-4 mr-2" />
                                                ) : (
                                                    <XCircle className="w-4 h-4 mr-2" />
                                                )}
                                                Demande supplétive : {aeisResult.message}
                                            </p>
                                        </div>
                                        {aeisResult.email_result && (
                                            <div className={`p-4 rounded-lg border ${aeisResult.email_result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                                                <p className={`text-sm font-medium flex items-center ${aeisResult.email_result.success ? 'text-green-700' : 'text-red-700'}`}>
                                                    {aeisResult.email_result.success ? (
                                                        <CheckCircle className="w-4 h-4 mr-2" />
                                                    ) : (
                                                        <XCircle className="w-4 h-4 mr-2" />
                                                    )}
                                                    Demande centrale : {aeisResult.email_result.message}
                                                </p>
                                            </div>
                                        )}

                                        {/* Demande supplétive document card */}
                                        {aeisResult.success && aeisResult.demande_suppletive && (
                                            <a
                                                href={getFileUrl(aeisResult.demande_suppletive)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="group flex items-center justify-between bg-white border-2 border-green-300 rounded-lg p-4 hover:border-green-500 hover:shadow-md transition-all"
                                            >
                                                <div className="flex items-center">
                                                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-green-200 transition-colors">
                                                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-semibold text-gray-900 group-hover:text-green-700 transition-colors">Consulter la demande supplétive</p>
                                                        <p className="text-xs text-gray-600">Cliquez pour ouvrir dans un nouvel onglet</p>
                                                    </div>
                                                </div>
                                                <Eye className="w-5 h-5 text-green-600 group-hover:scale-110 transition-transform" />
                                            </a>
                                        )}

                                        {/* Screenshot document card */}
                                        {aeisResult.screenshot && (
                                            <a
                                                href={`data:image/png;base64,${aeisResult.screenshot}`}
                                                download={`aeis_screenshot_${Date.now()}.png`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`group flex items-center justify-between bg-white border-2 ${aeisResult.success ? 'border-green-300 hover:border-green-500' : 'border-red-300 hover:border-red-500'} rounded-lg p-4 hover:shadow-md transition-all`}
                                            >
                                                <div className="flex items-center">
                                                    <div className={`w-12 h-12 ${aeisResult.success ? 'bg-green-100 group-hover:bg-green-200' : 'bg-red-100 group-hover:bg-red-200'} rounded-lg flex items-center justify-center mr-4 transition-colors`}>
                                                        <svg className={`w-6 h-6 ${aeisResult.success ? 'text-green-600' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <p className={`text-sm font-semibold text-gray-900 ${aeisResult.success ? 'group-hover:text-green-700' : 'group-hover:text-red-700'} transition-colors`}>Capture d'écran du formulaire</p>
                                                        <p className="text-xs text-gray-600">Cliquez pour télécharger</p>
                                                    </div>
                                                </div>
                                                <Download className={`w-5 h-5 ${aeisResult.success ? 'text-green-600' : 'text-red-600'} group-hover:scale-110 transition-transform`} />
                                            </a>
                                        )}
                                    </div>
                                )}

                                {customer.demande_suppletive && !aeisResult && (
                                    <a
                                        href={getFileUrl(customer.demande_suppletive)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group flex items-center justify-between bg-white border-2 border-green-300 rounded-lg p-4 hover:border-green-500 hover:shadow-md transition-all"
                                    >
                                        <div className="flex items-center">
                                            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-green-200 transition-colors">
                                                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-gray-900 group-hover:text-green-700 transition-colors">Demande supplétive déjà générée</p>
                                                <p className="text-xs text-gray-600">Cliquez pour ouvrir dans un nouvel onglet</p>
                                            </div>
                                        </div>
                                        <Eye className="w-5 h-5 text-green-600 group-hover:scale-110 transition-transform" />
                                    </a>
                                )}

                                {/* AEIS Loading State */}
                                {aeisIsSubmitting && (
                                    <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg text-center">
                                        <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
                                        <p className="text-sm font-medium text-blue-900">Soumission en cours...</p>
                                        <p className="text-xs text-blue-600 mt-1">Le formulaire est en train d'être rempli et l'email envoyé. Veuillez patienter.</p>
                                    </div>
                                )}

                                {/* Preview Merged Document Card */}
                                {(customer.procuration || customer.photo || customer.photo2) ? (
                                    <button
                                        onClick={async () => {
                                            try {
                                                const response = await customersApi.previewMergedDocument(customer.uuid);
                                                const blob = new Blob([response.data], { type: 'application/pdf' });
                                                const url = URL.createObjectURL(blob);
                                                window.open(url, '_blank');
                                            } catch {
                                                alert('Erreur lors de la génération du document fusionné');
                                            }
                                        }}
                                        className="group w-full flex items-center justify-between bg-white border-2 border-blue-300 rounded-lg p-4 hover:border-blue-500 hover:shadow-md transition-all text-left"
                                    >
                                        <div className="flex items-center">
                                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-blue-200 transition-colors">
                                                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">Consulter le document fusionné</p>
                                                <p className="text-xs text-gray-600">Procuration + pièces d'identité</p>
                                            </div>
                                        </div>
                                        <Eye className="w-5 h-5 text-blue-600 group-hover:scale-110 transition-transform" />
                                    </button>
                                ) : (
                                    <div className="w-full p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                                        <p className="text-sm text-yellow-700 flex items-center justify-center">
                                            <AlertCircle className="w-4 h-4 mr-2" />
                                            Aucun document disponible (procuration, photo manquants)
                                        </p>
                                    </div>
                                )}

                                {/* Submit / Validate Button */}
                                {aeisResult?.success && aeisResult?.email_result?.success ? (
                                    <button
                                        onClick={() => handleStateTransition(3)}
                                        disabled={stateTransitionIsPending}
                                        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3.5 rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-md hover:shadow-lg transition-all font-medium"
                                    >
                                        <CheckCircle className="w-5 h-5 mr-2" />
                                        Valider
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleState2Submit}
                                        disabled={isState2Submitting || stateTransitionIsPending || aeisIsSubmitting}
                                        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3.5 rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-md hover:shadow-lg transition-all font-medium"
                                    >
                                        {aeisIsSubmitting ? (
                                            <>
                                                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                                Soumission en cours...
                                            </>
                                        ) : isState2Submitting || stateTransitionIsPending ? (
                                            <>
                                                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                                Transition en cours...
                                            </>
                                        ) : (
                                            <>
                                                <ArrowRight className="w-5 h-5 mr-2" />
                                                Soumettre aux institutions
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* State 3 */}
                {currentState === 3 && (
                    <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                        <div className="bg-gradient-to-r from-amber-600 to-orange-600 px-6 py-4">
                            <h3 className="text-lg font-semibold text-white">État 3 - Attente réponses</h3>
                            <p className="text-xs text-amber-100 mt-1">Traitement des réponses institutions</p>
                        </div>
                        <div className="p-6">
                            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-5 mb-6">
                                <h4 className="text-sm font-semibold text-amber-900 mb-3 flex items-center">
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Tâches à effectuer
                                </h4>
                                <ul className="space-y-2.5">
                                    {!customer.reponse_suppletive && !customer.reponse_centrale ? (
                                        <>
                                            <li className="flex items-start text-sm text-amber-900">
                                                <span className="inline-block w-1.5 h-1.5 bg-amber-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                                                <span>Recevoir et traiter les réponses des institutions</span>
                                            </li>
                                            <li className="flex items-start text-sm text-amber-900">
                                                <span className="inline-block w-1.5 h-1.5 bg-amber-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                                                <span>Identifier le type de réponse (centrale ou supplétive)</span>
                                            </li>
                                        </>
                                    ) : customer.reponse_suppletive && customer.reponse_centrale ? (
                                        <>
                                            <li className="flex items-start text-sm text-amber-900">
                                                <span className={`inline-block w-1.5 h-1.5 ${centraleValidated ? 'bg-amber-600' : 'bg-amber-600'} rounded-full mr-3 mt-1.5 flex-shrink-0`}></span>
                                                <span className={centraleValidated ? 'line-through text-gray-500' : ''}>Valider la réponse centrale</span>
                                                {centraleValidated && <CheckCircle className="w-4 h-4 ml-2 text-amber-600" />}
                                            </li>
                                            <li className="flex items-start text-sm text-amber-900">
                                                <span className={`inline-block w-1.5 h-1.5 ${suppletiveValidated ? 'bg-amber-600' : 'bg-amber-600'} rounded-full mr-3 mt-1.5 flex-shrink-0`}></span>
                                                <span className={suppletiveValidated ? 'line-through text-gray-500' : ''}>Valider la réponse supplétive</span>
                                                {suppletiveValidated && <CheckCircle className="w-4 h-4 ml-2 text-amber-600" />}
                                            </li>
                                            <li className="flex items-start text-sm text-amber-900">
                                                <span className="inline-block w-1.5 h-1.5 bg-amber-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                                                <span>Passer à l'étape suivante du workflow</span>
                                            </li>
                                        </>
                                    ) : customer.reponse_suppletive ? (
                                        <>
                                            <li className="flex items-start text-sm text-amber-900">
                                                <span className="inline-block w-1.5 h-1.5 bg-amber-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                                                <span>Vérifier si des documents manquants sont demandés</span>
                                            </li>
                                            <li className="flex items-start text-sm text-amber-900">
                                                <span className="inline-block w-1.5 h-1.5 bg-amber-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                                                <span>Saisir le montant communiqué par l'institution</span>
                                            </li>
                                            <li className="flex items-start text-sm text-amber-900">
                                                <span className="inline-block w-1.5 h-1.5 bg-amber-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                                                <span>Traiter les informations de la réponse supplétive</span>
                                            </li>
                                        </>
                                    ) : (
                                        <>
                                            <li className="flex items-start text-sm text-amber-900">
                                                <span className="inline-block w-1.5 h-1.5 bg-amber-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                                                <span>Valider la réponse centrale reçue</span>
                                            </li>
                                            <li className="flex items-start text-sm text-amber-900">
                                                <span className="inline-block w-1.5 h-1.5 bg-amber-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                                                <span>Passer à l'étape suivante du workflow</span>
                                            </li>
                                        </>
                                    )}
                                </ul>
                            </div>

                        <div className="space-y-6">
                            {/* CASE: Both responses received - show both cards, centrale first */}
                            {customer.reponse_suppletive && customer.reponse_centrale ? (
                                <>
                                    {/* CENTRALE RESPONSE CARD */}
                                    <div className={`border-2 ${'border-amber-200 bg-amber-50'} rounded-lg p-5`}>
                                        {/* Header with validation status */}
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center">
                                                <div className={`w-10 h-10 bg-gradient-to-br ${'from-amber-500 to-orange-600'} rounded-lg flex items-center justify-center mr-3 shadow-md`}>
                                                    <FileText className="w-5 h-5 text-white" />
                                                </div>
                                                <div>
                                                    <h4 className={`text-sm font-semibold ${'text-amber-900'}`}>Réponse Centrale</h4>
                                                    <p className={`text-xs ${'text-amber-700'}`}>
                                                        {centraleValidated ? 'Validée' : 'À valider en premier'}
                                                    </p>
                                                </div>
                                            </div>
                                            {centraleValidated && (
                                                <div className="bg-amber-600 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center">
                                                    <CheckCircle className="w-3 h-3 mr-1" />
                                                    Validée
                                                </div>
                                            )}
                                        </div>

                                        {/* Document Consultation */}
                                        <a
                                            href={getFileUrl(customer.reponse_centrale)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`group flex items-center justify-between bg-white border-2 ${'border-amber-300'} rounded-lg p-4 hover:border-amber-500 hover:shadow-md transition-all mb-5`}
                                        >
                                            <div className="flex items-center">
                                                <div className={`w-12 h-12 ${'bg-amber-100'} rounded-lg flex items-center justify-center mr-4 group-hover:bg-amber-200 transition-colors`}>
                                                    <svg className={`w-6 h-6 ${'text-amber-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900 group-hover:text-amber-700 transition-colors">Consulter le document</p>
                                                    <p className="text-xs text-gray-600">Cliquez pour ouvrir dans un nouvel onglet</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <Eye className={`w-5 h-5 ${'text-amber-600'} group-hover:scale-110 transition-transform`} />
                                            </div>
                                        </a>

                                        {/* Centrale response actions - only show if not validated */}
                                        {!centraleValidated && (
                                            <div className="bg-white rounded-lg p-5 space-y-4">
                                                {renderCentraleActions()}
                                            </div>
                                        )}
                                    </div>

                                    {/* SUPPLETIVE RESPONSE CARD - only enabled after centrale is validated */}
                                    <div className={`border-2 ${!centraleValidated ? 'border-gray-200 bg-gray-50 opacity-60' : 'border-amber-200 bg-amber-50'} rounded-lg p-5`}>
                                        {/* Header with validation status */}
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center">
                                                <div className={`w-10 h-10 bg-gradient-to-br ${!centraleValidated ? 'from-gray-400 to-gray-500' : 'from-amber-500 to-orange-600'} rounded-lg flex items-center justify-center mr-3 shadow-md`}>
                                                    <FileText className="w-5 h-5 text-white" />
                                                </div>
                                                <div>
                                                    <h4 className={`text-sm font-semibold ${!centraleValidated ? 'text-gray-600' : 'text-amber-900'}`}>Réponse Supplétive</h4>
                                                    <p className={`text-xs ${!centraleValidated ? 'text-gray-500' : 'text-amber-700'}`}>
                                                        {!centraleValidated ? 'Valider d\'abord la réponse centrale' : suppletiveValidated ? 'Validée' : 'À valider'}
                                                    </p>
                                                </div>
                                            </div>
                                            {suppletiveValidated && (
                                                <div className="bg-amber-600 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center">
                                                    <CheckCircle className="w-3 h-3 mr-1" />
                                                    Validée
                                                </div>
                                            )}
                                        </div>

                                        {/* Document Consultation */}
                                        <a
                                            href={getFileUrl(customer.reponse_suppletive)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`group flex items-center justify-between bg-white border-2 ${!centraleValidated ? 'border-gray-200' : 'border-amber-300'} rounded-lg p-4 hover:shadow-md transition-all mb-5 ${!centraleValidated ? 'pointer-events-none' : ''}`}
                                        >
                                            <div className="flex items-center">
                                                <div className={`w-12 h-12 ${!centraleValidated ? 'bg-gray-100' : 'bg-amber-100'} rounded-lg flex items-center justify-center mr-4 transition-colors`}>
                                                    <svg className={`w-6 h-6 ${!centraleValidated ? 'text-gray-400' : 'text-amber-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <p className={`text-sm font-semibold ${!centraleValidated ? 'text-gray-500' : 'text-gray-900'} transition-colors`}>Consulter le document</p>
                                                    <p className="text-xs text-gray-600">Cliquez pour ouvrir dans un nouvel onglet</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <Eye className={`w-5 h-5 ${!centraleValidated ? 'text-gray-400' : 'text-amber-600'} transition-transform`} />
                                            </div>
                                        </a>

                                        {/* Suppletive response actions - only show if centrale validated and suppletive not validated */}
                                        {centraleValidated && !suppletiveValidated && (
                                            <div className="bg-white rounded-lg p-5 space-y-4">
                                                {renderSuppletiveActions()}
                                            </div>
                                        )}
                                    </div>

                                    {/* Final submit button - only when both are validated */}
                                    {centraleValidated && suppletiveValidated && (
                                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-5">
                                            <div className="flex items-center mb-4">
                                                <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
                                                <div>
                                                    <h4 className="text-sm font-semibold text-green-900">Toutes les réponses validées</h4>
                                                    <p className="text-xs text-green-700">Vous pouvez maintenant passer à l'état suivant</p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleStateTransition(4)}
                                                disabled={stateTransitionIsPending}
                                                className="w-full bg-green-600 text-white px-6 py-3.5 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center shadow-md hover:shadow-lg transition-all font-medium"
                                            >
                                                <ArrowRight className="w-5 h-5 mr-2" />
                                                Passer à l'état 4
                                            </button>
                                        </div>
                                    )}
                                </>
                            ) : customer.reponse_suppletive ? (
                                // ONLY SUPPLETIVE RESPONSE RECEIVED
                                <>
                                    <div className={`border-2 ${'border-amber-200 bg-amber-50'} rounded-lg p-5`}>
                                        {/* Header */}
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center">
                                                <div className={`w-10 h-10 bg-gradient-to-br ${'from-amber-500 to-orange-600'} rounded-lg flex items-center justify-center mr-3 shadow-md`}>
                                                    <FileText className="w-5 h-5 text-white" />
                                                </div>
                                                <div>
                                                    <h4 className={`text-sm font-semibold ${'text-amber-900'}`}>Réponse Supplétive reçue</h4>
                                                    <p className={`text-xs ${'text-amber-700'}`}>
                                                        {suppletiveValidated ? 'Validée - En attente de la réponse centrale' : 'Traitement de la réponse'}
                                                    </p>
                                                </div>
                                            </div>
                                            {suppletiveValidated && (
                                                <div className="bg-amber-600 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center">
                                                    <CheckCircle className="w-3 h-3 mr-1" />
                                                    Validée
                                                </div>
                                            )}
                                        </div>

                                        {/* Document Consultation */}
                                        <a
                                            href={getFileUrl(customer.reponse_suppletive)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`group flex items-center justify-between bg-white border-2 ${'border-amber-300'} rounded-lg p-4 hover:border-amber-500 hover:shadow-md transition-all mb-5`}
                                        >
                                            <div className="flex items-center">
                                                <div className={`w-12 h-12 ${'bg-amber-100'} rounded-lg flex items-center justify-center mr-4 group-hover:bg-amber-200 transition-colors`}>
                                                    <svg className={`w-6 h-6 ${'text-amber-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900 group-hover:text-amber-700 transition-colors">Consulter le document</p>
                                                    <p className="text-xs text-gray-600">Cliquez pour ouvrir dans un nouvel onglet</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <Eye className={`w-5 h-5 ${'text-amber-600'} group-hover:scale-110 transition-transform`} />
                                            </div>
                                        </a>

                                        {/* Show validation form if not validated */}
                                        {!suppletiveValidated && (
                                            <>
                                                {/* Missing documents checkbox */}
                                                <div className="mb-5 bg-white rounded-lg p-4 border border-amber-200">
                                                    <label className="flex items-center space-x-3 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={isMissingDocuments}
                                                            onChange={(e) => {
                                                                setIsMissingDocuments(e.target.checked);
                                                                if (e.target.checked) {
                                                                    setSelectedAmount(null);
                                                                    setHasOldTransfert(null);
                                                                    setTotalAmount('');
                                                                }
                                                            }}
                                                            className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                                                        />
                                                        <span className="text-sm font-medium text-gray-900">
                                                            Demande de documents manquants
                                                        </span>
                                                    </label>
                                                    <p className="text-xs text-gray-500 mt-1 ml-7">
                                                        Cochez si la réponse demande des documents supplémentaires
                                                    </p>
                                                </div>

                                                {/* If missing documents - show confirmation message */}
                                                {isMissingDocuments && (
                                                    <div className="bg-white rounded-lg p-4 border border-amber-200">
                                                        <p className="text-sm text-gray-700 mb-3">
                                                            Vérifiez que les documents demandés dans la réponse ont été fournis par le client.
                                                        </p>
                                                        <div className="flex items-center space-x-3">
                                                            <button
                                                                onClick={handleValidateSuppletive}
                                                                disabled={state3SubmissionIsPending}
                                                                className="flex-1 bg-amber-600 text-white px-4 py-2.5 rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors shadow-sm hover:shadow-md flex items-center justify-center"
                                                            >
                                                                {state3SubmissionIsPending ? (
                                                                    <>
                                                                        <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                        </svg>
                                                                        Validation...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <CheckCircle className="w-4 h-4 mr-2" />
                                                                        Documents fournis - Valider
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* If NOT missing documents - show amount selection */}
                                                {!isMissingDocuments && renderAmountSelection()}
                                            </>
                                        )}
                                    </div>

                                    {/* Waiting for centrale card */}
                                    <div className="border-2 border-gray-200 bg-gray-50 opacity-60 rounded-lg p-5 mt-6">
                                        <div className="flex items-center mb-4">
                                            <div className="w-10 h-10 bg-gradient-to-br from-gray-400 to-gray-500 rounded-lg flex items-center justify-center mr-3 shadow-md">
                                                <FileText className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <h4 className="text-sm font-semibold text-gray-600">Réponse Centrale</h4>
                                                <p className="text-xs text-gray-500">En attente de réception</p>
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
                                            <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                            <p className="text-sm text-gray-600">La réponse centrale n'a pas encore été reçue.</p>
                                            <p className="text-xs text-gray-500 mt-1">Les deux réponses sont nécessaires pour passer à l'état 4.</p>
                                        </div>
                                    </div>

                                    {/* Info message when suppletive is validated */}
                                    {suppletiveValidated && (
                                        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-5 mt-6">
                                            <div className="flex items-center">
                                                <AlertCircle className="w-6 h-6 text-amber-600 mr-3" />
                                                <div>
                                                    <h4 className="text-sm font-semibold text-amber-900">En attente de la réponse centrale</h4>
                                                    <p className="text-xs text-amber-700">La réponse supplétive est validée. Vous pourrez passer à l'état 4 une fois la réponse centrale reçue et validée.</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : customer.reponse_centrale ? (
                                // ONLY CENTRALE RESPONSE RECEIVED
                                <>
                                    <div className={`border-2 ${'border-amber-200 bg-amber-50'} rounded-lg p-5`}>
                                        {/* Header */}
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center">
                                                <div className={`w-10 h-10 bg-gradient-to-br ${'from-amber-500 to-orange-600'} rounded-lg flex items-center justify-center mr-3 shadow-md`}>
                                                    <FileText className="w-5 h-5 text-white" />
                                                </div>
                                                <div>
                                                    <h4 className={`text-sm font-semibold ${'text-amber-900'}`}>Réponse Centrale reçue</h4>
                                                    <p className={`text-xs ${'text-amber-700'}`}>
                                                        {centraleValidated ? 'Validée - En attente de la réponse supplétive' : 'Traitement de la réponse'}
                                                    </p>
                                                </div>
                                            </div>
                                            {centraleValidated && (
                                                <div className="bg-amber-600 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center">
                                                    <CheckCircle className="w-3 h-3 mr-1" />
                                                    Validée
                                                </div>
                                            )}
                                        </div>

                                        {/* Document Consultation */}
                                        <a
                                            href={getFileUrl(customer.reponse_centrale)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`group flex items-center justify-between bg-white border-2 ${'border-amber-300'} rounded-lg p-4 hover:border-amber-500 hover:shadow-md transition-all mb-5`}
                                        >
                                            <div className="flex items-center">
                                                <div className={`w-12 h-12 ${'bg-amber-100'} rounded-lg flex items-center justify-center mr-4 group-hover:bg-amber-200 transition-colors`}>
                                                    <svg className={`w-6 h-6 ${'text-amber-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900 group-hover:text-amber-700 transition-colors">Consulter le document</p>
                                                    <p className="text-xs text-gray-600">Cliquez pour ouvrir dans un nouvel onglet</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <Eye className={`w-5 h-5 ${'text-amber-600'} group-hover:scale-110 transition-transform`} />
                                            </div>
                                        </a>

                                        {/* Centrale response actions - only if not validated */}
                                        {!centraleValidated && (
                                            <div className="bg-white rounded-lg p-5 space-y-4">
                                                {renderCentraleActions()}
                                            </div>
                                        )}
                                    </div>

                                    {/* Waiting for suppletive card */}
                                    <div className="border-2 border-gray-200 bg-gray-50 opacity-60 rounded-lg p-5 mt-6">
                                        <div className="flex items-center mb-4">
                                            <div className="w-10 h-10 bg-gradient-to-br from-gray-400 to-gray-500 rounded-lg flex items-center justify-center mr-3 shadow-md">
                                                <FileText className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <h4 className="text-sm font-semibold text-gray-600">Réponse Supplétive</h4>
                                                <p className="text-xs text-gray-500">En attente de réception</p>
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
                                            <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                            <p className="text-sm text-gray-600">La réponse supplétive n'a pas encore été reçue.</p>
                                            <p className="text-xs text-gray-500 mt-1">Les deux réponses sont nécessaires pour passer à l'état 4.</p>
                                        </div>
                                    </div>

                                    {/* Info message when centrale is validated */}
                                    {centraleValidated && (
                                        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-5 mt-6">
                                            <div className="flex items-center">
                                                <AlertCircle className="w-6 h-6 text-amber-600 mr-3" />
                                                <div>
                                                    <h4 className="text-sm font-semibold text-amber-900">En attente de la réponse supplétive</h4>
                                                    <p className="text-xs text-amber-700">La réponse centrale est validée. Vous pourrez passer à l'état 4 une fois la réponse supplétive reçue et validée.</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                // NO RESPONSE YET
                                <div className="border-2 border-gray-200 bg-gray-50 rounded-lg p-5 text-center">
                                    <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center mx-auto mb-3">
                                        <AlertCircle className="w-6 h-6 text-gray-400" />
                                    </div>
                                    <h4 className="text-sm font-semibold text-gray-900 mb-2">En attente de réponse</h4>
                                    <p className="text-xs text-gray-600">
                                        Aucune réponse (centrale ou supplétive) n'a encore été reçue pour ce dossier.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                )}
            </div>

            {/* RIGHT COLUMN - Intervention (1/2 width) */}
            <div className="space-y-6">
                {/* Active Intervention Display */}
                {customer?.intervention_conseiller && customer?.latest_active_intervention && (
                    <div className="bg-red-50 border-2 border-red-500 rounded-lg shadow-md overflow-hidden">
                        <div className="bg-red-600 px-6 py-4">
                            <h3 className="text-lg font-semibold text-white flex items-center">
                                <MessageSquare className="w-5 h-5 mr-2 animate-pulse" />
                                Intervention ouverte
                            </h3>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="bg-white rounded-lg p-4 border border-red-200">
                                <p className="text-xs text-gray-500 mb-2">État d'origine:</p>
                                <p className="text-sm font-medium text-gray-900 mb-3">
                                    {getStateName(customer.state_before_intervention)}
                                </p>

                                <p className="text-xs text-gray-500 mb-2">Type d'intervention:</p>
                                <p className="text-sm font-medium text-gray-900 mb-3">
                                    {getInterventionTypeLabel(customer.latest_active_intervention.intervention_type)}
                                </p>

                                <p className="text-xs text-gray-500 mb-2">Message:</p>
                                <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded border border-gray-200">
                                    {customer.latest_active_intervention.message}
                                </p>

                                <p className="text-xs text-gray-500 mt-3">
                                    Créée le {new Date(customer.latest_active_intervention.created_at).toLocaleDateString('fr-FR')}
                                    {customer.latest_active_intervention.created_by && ` par ${customer.latest_active_intervention.created_by}`}
                                </p>
                            </div>
                            <div className="bg-red-100 border border-red-300 rounded-lg p-3">
                                <p className="text-sm text-red-700 font-medium flex items-center">
                                    <AlertCircle className="w-4 h-4 mr-2" />
                                    En attente de réponse du conseiller
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Resolved Intervention Info */}
                {customer?.state_before_intervention && !customer?.intervention_conseiller && (
                    <div className="bg-purple-50 border-2 border-purple-300 rounded-lg shadow-md overflow-hidden">
                        <div className="bg-purple-600 px-6 py-4">
                            <h3 className="text-lg font-semibold text-white flex items-center">
                                <CheckCircle className="w-5 h-5 mr-2" />
                                Intervention résolue
                            </h3>
                        </div>
                        <div className="p-6">
                            <div className="bg-white rounded-lg p-4 border border-purple-200">
                                {customer.latest_resolved_intervention && (
                                    <div className="space-y-3">
                                        <div>
                                            <p className="text-xs text-gray-500 mb-2">Type d'intervention:</p>
                                            <p className="text-sm font-medium text-gray-900 mb-3">
                                                {getInterventionTypeLabel(customer.latest_resolved_intervention.intervention_type)}
                                            </p>
                                        </div>

                                        <div>
                                            <p className="text-xs text-gray-500 mb-2">Message initial:</p>
                                            <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded border border-gray-200 mb-3">
                                                {customer.latest_resolved_intervention.message}
                                            </p>
                                        </div>

                                        {customer.latest_resolved_intervention.response && (
                                            <div>
                                                <p className="text-xs text-gray-500 mb-2">Réponse du conseiller:</p>
                                                <p className="text-sm text-gray-700 bg-purple-50 p-3 rounded border border-purple-200">
                                                    {customer.latest_resolved_intervention.response}
                                                </p>
                                            </div>
                                        )}

                                        <p className="text-xs text-gray-500 pt-2">
                                            Répondu le {new Date(customer.latest_resolved_intervention.responded_at).toLocaleDateString('fr-FR')}
                                            {' '}par {customer.latest_resolved_intervention.responded_by}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Intervention conseiller */}
                <div className="bg-white rounded-lg shadow border border-gray-200">
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <MessageSquare className="w-5 h-5 mr-2 text-gray-600" />
                            Intervention conseiller
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                            Demandez une intervention conseiller pour signaler un problème ou demander de l'aide
                        </p>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-5">
                        {/* Button */}
                        <button
                            onClick={() => setShowInterventionModal(true)}
                            disabled={!!customer?.intervention_conseiller}
                            className="w-full bg-gray-900 text-white px-6 py-3.5 rounded-lg hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center shadow-sm hover:shadow transition-all font-medium"
                        >
                            <MessageSquare className="w-5 h-5 mr-2" />
                            Créer une intervention
                        </button>

                        {/* Show reason why button is disabled */}
                        {customer?.intervention_conseiller && (
                            <p className="text-xs text-gray-500 text-center mt-2">
                                Une intervention est déjà en cours
                            </p>
                        )}

                        {/* Comment section */}
                        <div>
                            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                                <FileText className="w-4 h-4 mr-2 text-gray-500" />
                                Notes & Commentaires
                            </label>
                            <textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                rows={8}
                                className="w-full px-4 py-3 rounded-lg border border-gray-300 shadow-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors resize-none"
                                placeholder="Ajoutez vos notes sur ce dossier client..."
                            />
                            <p className="text-xs text-gray-500 mt-2">
                                Ces notes sont visibles par toute l'équipe
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Helper render functions for reusable parts

    function renderCentraleActions() {
        return (
            <>
                {/* Checkbox for no caisse case */}
                <div className="flex items-start">
                    <div className="flex items-center h-5">
                        <input
                            id="no-caisse-checkbox"
                            type="checkbox"
                            checked={noCaisseInResponse}
                            onChange={(e) => {
                                setNoCaisseInResponse(e.target.checked);
                                if (e.target.checked) {
                                    setSelectedCaisses([]);
                                }
                            }}
                            className="w-4 h-4 text-amber-600 bg-gray-100 border-gray-300 rounded focus:ring-amber-500 focus:ring-2"
                        />
                    </div>
                    <div className="ml-3">
                        <label htmlFor="no-caisse-checkbox" className="text-sm font-medium text-gray-900 cursor-pointer">
                            Aucune caisse dans la réponse
                        </label>
                        <p className="text-xs text-gray-600 mt-0.5">
                            Cocher si la réponse centrale ne contient aucune caisse de pension
                        </p>
                    </div>
                </div>

                {/* Multiselect for caisses */}
                {!noCaisseInResponse && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center">
                            <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Sélectionner les caisses à traiter
                        </label>

                        <div className="relative mb-2">
                            <input
                                type="text"
                                value={caisseSearch}
                                onChange={(e) => setCaisseSearch(e.target.value)}
                                placeholder="Tapez pour rechercher..."
                                className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm transition-all"
                            />
                            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                        </div>

                        <div className="border-2 border-gray-300 rounded-lg bg-white shadow-sm overflow-hidden">
                            <div className="max-h-48 overflow-y-auto">
                            {caissesList
                                .filter((caisse) =>
                                    caisse.caisse.toLowerCase().includes(caisseSearch.toLowerCase())
                                )
                                .map((caisse) => {
                                    const isSelected = selectedCaisses.includes(caisse.id.toString());
                                    return (
                                        <div
                                            key={caisse.id}
                                            className={`border-b border-gray-100 last:border-b-0 transition-all duration-150 ${
                                                isSelected
                                                    ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-l-amber-600'
                                                    : 'hover:bg-gray-50 hover:border-l-4 hover:border-l-gray-300'
                                            }`}
                                        >
                                            <label className="flex items-start p-4 cursor-pointer">
                                                <div className="flex items-center pt-0.5">
                                                    <div className={`w-2 h-2 rounded-full mr-3 ${
                                                        isSelected ? 'bg-amber-600' : 'bg-gray-300'
                                                    }`}></div>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedCaisses([...selectedCaisses, caisse.id.toString()]);
                                                            } else {
                                                                setSelectedCaisses(selectedCaisses.filter(id => id !== caisse.id.toString()));
                                                            }
                                                        }}
                                                        className="w-4 h-4 text-amber-600 bg-gray-100 border-gray-300 rounded focus:ring-amber-500 focus:ring-2"
                                                    />
                                                </div>
                                                <div className="ml-3 flex-1">
                                                    <div className="flex items-center justify-between">
                                                        <span className={`text-sm font-medium ${
                                                            isSelected ? 'text-amber-900' : 'text-gray-900'
                                                        }`}>
                                                            {caisse.caisse}
                                                        </span>
                                                        {isSelected && (
                                                            <CheckCircle className="w-5 h-5 text-amber-600 ml-2" />
                                                        )}
                                                    </div>
                                                </div>
                                            </label>
                                        </div>
                                    );
                                })
                            }
                            </div>
                        </div>
                        {selectedCaisses.length > 0 && (
                            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                                <p className="text-xs text-amber-700 font-medium flex items-center">
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    {selectedCaisses.length} caisse(s) sélectionnée(s) pour traitement
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* Validate button for centrale */}
                {(noCaisseInResponse || selectedCaisses.length > 0) && (
                    <div className="pt-4 border-t border-gray-200">
                        <button
                            onClick={handleValidateCentrale}
                            disabled={state3SubmissionIsPending}
                            className="w-full bg-amber-600 text-white px-6 py-3.5 rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-md hover:shadow-lg transition-all font-medium"
                        >
                            {state3SubmissionIsPending ? (
                                <>
                                    <svg className="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Validation en cours...
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="w-5 h-5 mr-2" />
                                    Valider la réponse centrale
                                </>
                            )}
                        </button>
                    </div>
                )}
            </>
        );
    }

    function renderSuppletiveActions() {
        return (
            <>
                {/* Missing documents checkbox */}
                <div className="mb-4">
                    <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={isMissingDocuments}
                            onChange={(e) => {
                                setIsMissingDocuments(e.target.checked);
                                if (e.target.checked) {
                                    setSelectedAmount(null);
                                    setHasOldTransfert(null);
                                    setTotalAmount('');
                                }
                            }}
                            className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                        />
                        <span className="text-sm font-medium text-gray-900">
                            Demande de documents manquants
                        </span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1 ml-7">
                        Cochez si la réponse demande des documents supplémentaires
                    </p>
                </div>

                {/* If missing documents - show confirmation */}
                {isMissingDocuments && (
                    <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                        <p className="text-sm text-gray-700 mb-3">
                            Vérifiez que les documents demandés ont été fournis.
                        </p>
                        <button
                            onClick={handleValidateSuppletive}
                            disabled={state3SubmissionIsPending}
                            className="w-full bg-amber-600 text-white px-4 py-2.5 rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors shadow-sm hover:shadow-md flex items-center justify-center"
                        >
                            {state3SubmissionIsPending ? (
                                <>
                                    <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Validation...
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Documents fournis - Valider
                                </>
                            )}
                        </button>
                    </div>
                )}

                {/* If NOT missing documents - show amount selection */}
                {!isMissingDocuments && renderAmountSelection()}
            </>
        );
    }

    function renderAmountSelection() {
        return (
            <div className="space-y-4">
                <div className="bg-white rounded-lg p-4 border border-amber-200">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                        Montant de la réponse
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                        <button
                            onClick={() => {
                                setSelectedAmount('more');
                                setHasOldTransfert(null);
                            }}
                            className={`px-4 py-3 border-2 rounded-lg text-sm font-medium transition-colors ${
                                selectedAmount === 'more'
                                    ? 'bg-amber-600 border-amber-600 text-white'
                                    : 'bg-white border-amber-300 text-amber-700 hover:bg-amber-50'
                            }`}
                        >
                            &gt; CHF 1'300
                        </button>
                        <button
                            onClick={() => {
                                setSelectedAmount('less');
                                setHasOldTransfert(null);
                            }}
                            className={`px-4 py-3 border-2 rounded-lg text-sm font-medium transition-colors ${
                                selectedAmount === 'less'
                                    ? 'bg-amber-600 border-amber-600 text-white'
                                    : 'bg-white border-amber-300 text-amber-700 hover:bg-amber-50'
                            }`}
                        >
                            &lt; CHF 1'300
                        </button>
                        <button
                            onClick={() => {
                                setSelectedAmount('zero');
                                setHasOldTransfert(null);
                            }}
                            className={`px-4 py-3 border-2 rounded-lg text-sm font-medium transition-colors ${
                                selectedAmount === 'zero'
                                    ? 'bg-amber-600 border-amber-600 text-white'
                                    : 'bg-white border-amber-300 text-amber-700 hover:bg-amber-50'
                            }`}
                        >
                            CHF 0
                        </button>
                    </div>
                </div>

                {/* If amount is ZERO - ask for old transfert */}
                {selectedAmount === 'zero' && (
                    <div className="bg-white rounded-lg p-4 border border-amber-200">
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Y a-t-il un ancien transfert ?
                        </label>
                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <button
                                onClick={() => setHasOldTransfert(true)}
                                className={`px-4 py-2.5 border-2 rounded-lg text-sm font-medium transition-colors ${
                                    hasOldTransfert === true
                                        ? 'bg-amber-600 border-amber-600 text-white'
                                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                Oui
                            </button>
                            <button
                                onClick={() => setHasOldTransfert(false)}
                                className={`px-4 py-2.5 border-2 rounded-lg text-sm font-medium transition-colors ${
                                    hasOldTransfert === false
                                        ? 'bg-amber-600 border-amber-600 text-white'
                                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                Non
                            </button>
                        </div>

                        {hasOldTransfert === true && (
                            <div className="pt-4 border-t border-gray-200 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center">
                                        Sélectionner la caisse de pension
                                    </label>
                                    <div className="relative mb-2">
                                        <input
                                            type="text"
                                            value={pensionFundSearch}
                                            onChange={(e) => setPensionFundSearch(e.target.value)}
                                            placeholder="Tapez pour rechercher..."
                                            className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm transition-all"
                                        />
                                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="border-2 border-gray-300 rounded-lg bg-white shadow-sm overflow-hidden">
                                        <div className="max-h-40 overflow-y-auto">
                                            {caissesList
                                                .filter((fund) =>
                                                    fund.caisse.toLowerCase().includes(pensionFundSearch.toLowerCase())
                                                )
                                                .map((fund) => (
                                                    <button
                                                        key={fund.id}
                                                        type="button"
                                                        onClick={() => {
                                                            setSelectedPensionFund(fund.id.toString());
                                                            setPensionFundSearch(fund.caisse);
                                                        }}
                                                        className={`w-full text-left px-4 py-3 transition-all duration-150 border-b border-gray-100 last:border-b-0 ${
                                                            selectedPensionFund === fund.id.toString()
                                                                ? 'bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 font-medium border-l-4 border-l-amber-600'
                                                                : 'text-gray-700 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-sm">{fund.caisse}</span>
                                                            {selectedPensionFund === fund.id.toString() && (
                                                                <CheckCircle className="w-5 h-5 text-amber-600" />
                                                            )}
                                                        </div>
                                                    </button>
                                                ))}
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Montant
                                    </label>
                                    <input
                                        type="number"
                                        value={oldTransfertAmount}
                                        onChange={(e) => setOldTransfertAmount(e.target.value)}
                                        placeholder="CHF"
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
                                    />
                                </div>
                            </div>
                        )}

                        {hasOldTransfert === false && (
                            <div className="pt-4 border-t border-gray-200">
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                    <p className="text-sm text-amber-700 flex items-center">
                                        <CheckCircle className="w-4 h-4 mr-2" />
                                        Prêt pour validation
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* If amount is NOT ZERO - ask for total amount */}
                {(selectedAmount === 'more' || selectedAmount === 'less') && (
                    <div className="bg-white rounded-lg p-4 border border-amber-200">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Montant total
                        </label>
                        <input
                            type="number"
                            value={totalAmount}
                            onChange={(e) => setTotalAmount(e.target.value)}
                            placeholder="CHF"
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                            Saisir le montant total communiqué dans la réponse
                        </p>
                    </div>
                )}

                {/* Validate button for suppletive */}
                {(
                    (selectedAmount === 'more' && totalAmount) ||
                    (selectedAmount === 'less' && totalAmount) ||
                    (selectedAmount === 'zero' && hasOldTransfert === false) ||
                    (selectedAmount === 'zero' && hasOldTransfert === true && selectedPensionFund && oldTransfertAmount)
                ) && (
                    <div className="pt-4 border-t border-gray-200">
                        <button
                            onClick={handleValidateSuppletive}
                            disabled={state3SubmissionIsPending}
                            className="w-full bg-amber-600 text-white px-6 py-3.5 rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-md hover:shadow-lg transition-all font-medium"
                        >
                            {state3SubmissionIsPending ? (
                                <>
                                    <svg className="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Validation en cours...
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="w-5 h-5 mr-2" />
                                    Valider la réponse supplétive
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
        );
    }
};

export default WorkflowActionsTab;
